import { Injectable } from '@angular/core';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { onError } from '@apollo/client/link/error';
import { environment } from '../environments/environment';
import { createScApolloLink } from './common/api/CreateScApolloLink';
import { createTypePolicyFields } from './common/api/create-type-policy-fields';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './common/services/auth.service';
import { CookieService } from 'ngx-cookie';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root'
})
export class GraphqlProvider {
  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private cookies: CookieService,
    private toastr: ToastrService
  ) {}

  createApollo() {
    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const { muteError, error } = operation.getContext();
      if (muteError) {
        return;
      }
      if (graphQLErrors?.length) {
        graphQLErrors.forEach(({ message, locations, path }) => this.toastr.error(message));
      } else if (error) {
        this.toastr.error(error);
      } else {
        this.toastr.error('Something went wrong');
      }
      if (networkError) {
        console.error(networkError?.message);
      }
    });

    const isDevMode: boolean = !environment.production;

    return {
      link: ApolloLink.from([
        errorLink,
        createScApolloLink({
          fetchUri: environment.endpoints.gqlv3,
          httpClient: this.httpClient,
          authService: this.authService,
          cookies: this.cookies
        })
      ]),
      cache: new InMemoryCache({
        addTypename: true,
        possibleTypes: {
          AnyStepType: ['QuestionnaireStepType', 'CaseComponentType']
        },
        typePolicies: {
          Query: {
            fields: createTypePolicyFields()
          },
          CaseComponentType: {
            // This ensures duplicate case components will keep their unique "sets" values
            // All duplicates inherit the first component's "sets" value if we don't check for it here
            keyFields: (object, context) => {
              const fields = ['id'];
              if (object.hasOwnProperty('sets')) {
                fields.push('sets');
              }

              return fields;
            }
          }
        }
      }),
      connectToDevTools: isDevMode,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network'
        }
      }
    };
  }
}

export const graphqlProvider = [
  {
    provide: APOLLO_OPTIONS,
    useFactory: (graphQLProvider: GraphqlProvider) => graphQLProvider.createApollo(),
    deps: [GraphqlProvider]
  }
];
